import React, { useEffect, useRef, useId, useState } from "react";
import styled, { css } from "styled-components";
import AddonListItem from "./AddonListItem";
import { focusRingStyles } from "../../GlobalWidgetStyle";
import Skeleton from "../../skeleton/Skeleton";
import SkeletonText from "../../skeleton/SkeletonText";
import Flex from "../../ui/Flex";
import Stack from "../../ui/Stack";
import Text from "../../ui/Text";
import TextCircleDivider from "../../ui/TextCircleDivider";
import Badge from "../../ui/Badge";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import FormatPrice from "../../formatters/FormatPrice";
import FormatDuration from "../../formatters/FormatDuration";
import { useAssetsSrc } from "../../../hooks/useAssetsSrc";
import { useClickableA11y } from "../../../hooks/a11y/useClickableA11y";
import { usePreparedAddonsForService } from "../../../hooks/usePreparedAddonsForService";
import { useTranslate } from "../../../hooks/useTranslate";
import { useConfig } from "../../../lib/react-query/useConfig";

const StyledCard = styled.div`
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 1rem;
  overflow: hidden;
`;

const Header = styled(Flex)`
  padding: 1rem;
  gap: 1rem;
  transition: background 0.2s ease;

  &:focus-visible {
    outline-offset: -2px !important;
    border-radius: inherit;
  }

  ${({ $clickable, theme }) => $clickable && css`
    cursor: pointer;

    &:hover {
      background: rgba(${theme.colors.accent.rgb}, 0.08);
    }
  `}

  ${({ theme }) => theme.containerMedia.sm} {
    flex-wrap: wrap;
  }
`;

const HeaderInfo = styled(Flex)`
  flex: 1;
  min-width: 0;

  ${({ theme }) => theme.containerMedia.sm} {
    flex-basis: 100%;
  }
`;

const HeaderInfoStack = styled(Stack)`
  flex: 1;
  min-width: 0;
`;

const HeaderActions = styled(Flex)`
  flex-shrink: 0;
  margin-left: auto;
`;

const HeaderMeta = styled(Flex)`
  ${({ theme }) => theme.containerMedia.sm} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.125rem;
  }
`;

const StyledTextCircleDivider = styled((props) => <TextCircleDivider {...props} />)`
  ${({ theme }) => theme.containerMedia.sm} {
    display: none;
  }
`;

const Media = styled(Flex)`
  width: 56px;
  height: 56px;
  border-radius: 0.5rem;
  flex-shrink: 0;
  overflow: hidden;
  background-color: rgba(${({ theme }) => theme.colors.border.rgb}, 0.5);
`;

const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Divider = styled.div`
   border-top: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
`;

const LoadMoreRow = styled(Flex)`
  padding: 1rem;
`;

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 1rem;
  padding: 1rem;

  ${({ theme }) => theme.containerMedia.sm} {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const SkeletonContent = styled(Stack)`
  flex: 1;
  min-width: 0;
`;

const AddButtonSkeleton = styled(Skeleton)`
  width: 90px;
  height: 36px;

  ${({ theme }) => theme.containerMedia.sm} {
    width: 100%;
    height: 42px;
  }
`;

const AddonRowSkeleton = () => (
  <SkeletonRow>
    <Flex $gap="1rem">
      <Skeleton borderRadius={8} width={56} height={56} />
      <SkeletonContent $gap="0.25rem">
        <SkeletonText width="40%" />
        <SkeletonText size="small" width="70%" />
        <SkeletonText width="30%" />
      </SkeletonContent>
    </Flex>
    <AddButtonSkeleton borderRadius={8} />
  </SkeletonRow>
);

const lowerFirst = (str) => str.charAt(0).toLowerCase() + str.slice(1);

const REVEAL_STEP = 10;
const EXCLUSIVE_NOTE_TIMEOUT = 5000;

const ServiceAddonsCard = ({
  service, isExpanded, onToggle,
  search, orderField, orderDirection,
  handleProductSelect, updateProduct, addDefaultAddons,
  isExclusiveNoteShown = false, onExclusiveNoteShown,
}) => {
  const { data: config } = useConfig();
  const isExclusive = !!config?.paid_attributes_exclusive;
  const { addons, totalCount, isLoading } =
    usePreparedAddonsForService(service, { search, orderField, orderDirection, addDefaultAddons, exclusive: isExclusive });
  const maxSelectableCount = isExclusive ? 1 : totalCount;
  const [visibleCount, setVisibleCount] = useState(REVEAL_STEP);
  const [noteAddonId, setNoteAddonId] = useState(null);
  const noteTimerRef = useRef(null);
  const { _t } = useTranslate();
  const contentId = useId();
  const headerA11yProps = useClickableA11y({ onClick: onToggle });

  const image = useAssetsSrc(service.picture_preview);
  const selectedCount = service.addons?.length || 0;

  useEffect(() => () => clearTimeout(noteTimerRef.current), []);

  const handleAddonSelect = (position, product) => {
    const isAdding = !service.addons?.some((a) => +a.id === +product.id);
    const isReplacing = isAdding && selectedCount > 0;
    handleProductSelect(position, product, isExclusive);

    if (!isExclusive) return;
    if (isReplacing && !isExclusiveNoteShown) {
      onExclusiveNoteShown?.();
      setNoteAddonId(product.id);
      noteTimerRef.current = setTimeout(() => setNoteAddonId(null), EXCLUSIVE_NOTE_TIMEOUT);
    } else {
      clearTimeout(noteTimerRef.current);
      setNoteAddonId(null);
    }
  };

  const hasAddons = service.has_available_addons && (isLoading || totalCount > 0);
  const showList = isExpanded && hasAddons;
  const showEmptyMessage = isExpanded && !hasAddons;

  const badge = isLoading
    ? null
    : !hasAddons
      ? { title: _t("No add-ons"), color: "neutral" }
      : selectedCount > 0
        ? { title: `${_t("%number% of %count% added", { number: selectedCount, count: maxSelectableCount })}`, color: "primary" }
        : { title: `${totalCount} ${lowerFirst(_t("Available"))}`, color: "primary" };

  return (
    <StyledCard>
      <Header
        $align="center"
        $justify="space-between"
        $clickable
        onClick={onToggle}
        {...headerA11yProps}
        aria-expanded={isExpanded}
        aria-controls={isExpanded ? contentId : undefined}
      >
        <HeaderInfo $align="center" $gap="1rem">
          <Media $align="center" $justify="center">
            {image
              ? <MediaImage src={image} alt="" />
              : <Icon.ServicePlaceholderIcon size={Icon.SIZE.LG} aria-hidden="true" />}
          </Media>
          <HeaderInfoStack $gap="0.25rem">
            <Text color="dark" weight="bold">{service.name}</Text>
            <HeaderMeta $align="center" $gap="0.25rem">
              {!!service.price && (
                <Text>
                  <FormatPrice value={service.price} currency={service.currency} />
                </Text>
              )}
              {!!service.price && !!service.duration && <StyledTextCircleDivider />}
              {!!service.duration && (
                <Text>
                  <FormatDuration value={service.duration} type={FormatDuration.TYPE.HOUR} />
                </Text>
              )}
            </HeaderMeta>
          </HeaderInfoStack>
        </HeaderInfo>

        <HeaderActions $align="center" $gap="1rem">
          {badge && <Badge {...badge} />}
          {isExpanded
            ? <Icon.ChevronUp size={Icon.SIZE.SM} color="secondary" aria-hidden="true" />
            : <Icon.ChevronDown size={Icon.SIZE.SM} color="secondary" aria-hidden="true" />}
        </HeaderActions>
      </Header>

      {showList && (
        <div id={contentId}>
          {isLoading && !addons.length
            ? <>
                <Divider /><AddonRowSkeleton />
                <Divider /><AddonRowSkeleton />
                <Divider /><AddonRowSkeleton />
              </>
            : addons.slice(0, visibleCount).map((addon) => (
              <React.Fragment key={addon.id}>
                <Divider />
                <AddonListItem
                  product={addon}
                  position={service.id}
                  selectedProducts={service.addons}
                  handleProductSelect={handleAddonSelect}
                  updateProduct={updateProduct}
                  addedLabel="Added"
                  noteText={isExclusive ? _t("Only one add-on can be selected at a time") : null}
                  isNoteVisible={noteAddonId === addon.id}
                />
              </React.Fragment>
            ))}

          {addons.length > visibleCount && (
            <>
              <Divider />
              <LoadMoreRow $justify="center">
                <Button title="Load more" variant="outline" onClick={() => setVisibleCount((count) => count + REVEAL_STEP)} />
              </LoadMoreRow>
            </>
          )}
        </div>
      )}

      {showEmptyMessage && (
        <div id={contentId}>
          <Divider />
          <LoadMoreRow $justify="center">
            <Text size="small">{_t("No add-ons available for this service")}</Text>
          </LoadMoreRow>
        </div>
      )}
    </StyledCard>
  );
};

export default ServiceAddonsCard;
